import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import {
  postCopyProjectTemplate,
  postProjectTemplate,
  updateProjectTemplate,
} from 'api/projectTemplates'
import { INTERVIEW_MOUDLE_FIELD } from 'components/modals/common/JobPositionModal/tabs/Modules/model'
import { mapInternalStateToPayload } from 'components/modals/common/JobPositionModal/actions'
import { TAB_NAMES } from 'components/pages/Templates/model'
import { getLocation, toTemplates } from 'ducks/router'
import { useModal, useSearch, useSnackbar } from 'hooks'
import { getDefaultTemplate } from 'components/pages/Jobs/JobListing/hooks'
import { jobTemplates, interviewTemplates } from 'orm'
import type { ApiErrorHandler, MutationCallbacks } from 'orm/types'

type JobTemplate = { id: number | string; [key: string]: unknown }

type FormCallbacks = { onError: ApiErrorHandler; onSuccess: () => void }

const useQueries = () => {
  const dispatch = useDispatch()

  const handleGetJobTemplates = React.useCallback(
    ({ limit, department, offset, ordering, search }) => {
      dispatch(
        jobTemplates.fetchStart({
          limit,
          department,
          offset,
          ordering,
          search,
        }),
      )
    },
    [dispatch],
  )

  return { handleGetJobTemplates }
}

const useMutations = () => {
  const dispatch = useDispatch()

  const handleDeleteJobTemplate = (
    { templateId },
    { postSuccessAction }: Pick<MutationCallbacks, 'postSuccessAction'> = {},
  ) => {
    dispatch(
      jobTemplates.deleteStart({
        message: t`Job template was successfully deleted.`,
        pk: String(templateId),
        postSuccessAction,
      }),
    )
  }

  // TODO: use if API endpoint will be implemented
  const handlePostCopyJobTemplate = async ({ title, department, uuid }) =>
    await postCopyProjectTemplate({ title, department, uuid })

  const handlePostJobTemplate = async ({
    category,
    department,
    description,
    languages,
    interview_template,
    modules,
    name,
    profile,
  }) =>
    await postProjectTemplate({
      category,
      department,
      description,
      languages,
      interview_template,
      modules,
      name,
      profile,
    })

  const handlePatchJobTemplate = async (
    id,
    {
      category,
      department,
      description,
      languages,
      interview_template,
      modules,
      name,
      profile,
    },
  ) =>
    await updateProjectTemplate(id, {
      category,
      department,
      description,
      languages,
      interview_template,
      modules,
      name,
      profile,
    })

  return {
    handleDeleteJobTemplate,
    handlePatchJobTemplate,
    handlePostCopyJobTemplate,
    handlePostJobTemplate,
  }
}

const useManagementJobTemplateModal = (state, handlers) => {
  const dispatch = useDispatch()

  const { query, template } = state
  const {
    handleGetJobTemplates,
    handlePatchJobTemplate,
    handlePostJobTemplate,
    handleResetTemplate,
    handleShowSnackbar,
  } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleManagementJobTemplateClose = React.useCallback(() => {
    handleClose()
    handleResetTemplate()
  }, [handleClose, handleResetTemplate])

  const handleCreateJobTemplate = React.useCallback(
    async (data, { onError }: FormCallbacks) => {
      const payload = mapInternalStateToPayload(data)

      const templateValue = data?.template?.value
      const templatePosition = data?.position
      const hasInterviewModule = !!data?.modules.find(
        module => module.name === INTERVIEW_MOUDLE_FIELD,
      )[INTERVIEW_MOUDLE_FIELD]

      const postJobTemplate = async (extendedPayload?: {
        interview_template: string
      }) => {
        const { error, meta, response } = await handlePostJobTemplate({
          ...payload,
          ...extendedPayload,
        })

        if (response) {
          handleManagementJobTemplateClose()
          handleShowSnackbar({
            message: extendedPayload
              ? t`Job template and interview template were successfully created.`
              : t`Job template was successfully created.`,
            type: 'success',
          })
          dispatch(toTemplates(TAB_NAMES.JOBS, response?.id))
        } else {
          onError({
            message: error,
            type: meta.HTTPstatus,
          })
        }
      }

      // if default template was selected by the user
      // then create it and afterwards create a job template
      if (hasInterviewModule && !templateValue) {
        dispatch(
          interviewTemplates.postStart({
            data: getDefaultTemplate(data, templatePosition),
            noSnackbar: true,
            postSuccessAction: () =>
              postJobTemplate({
                interview_template: `${templatePosition} ${t`Template`}`,
              }),
          }),
        )
      } else {
        postJobTemplate()
      }
    },
    [
      dispatch,
      handleManagementJobTemplateClose,
      handlePostJobTemplate,
      handleShowSnackbar,
    ],
  )

  const handleEditJobTemplate = React.useCallback(
    async (data, { onError, onSuccess }: FormCallbacks) => {
      const payload = mapInternalStateToPayload(data)

      const templateValue = data?.template?.value
      const templatePosition = data?.position
      const hasInterviewModule = !!data?.modules.find(
        module => module.name === INTERVIEW_MOUDLE_FIELD,
      )[INTERVIEW_MOUDLE_FIELD]

      const patchJobTemplate = async (extendedPayload?: {
        interview_template: string
      }) => {
        const { error, meta, response } = await handlePatchJobTemplate(
          template?.id,
          {
            ...payload,
            ...extendedPayload,
          },
        )

        if (response) {
          handleClose()
          handleResetTemplate()
          onSuccess()
          handleShowSnackbar({
            message: t`Job template was successfully updated.`,
            type: 'success',
          })
        } else {
          onError({
            message: error,
            type: meta.HTTPstatus,
          })
        }
      }

      // if default template was selected by the user
      // then create it and afterwards create a job template
      if (hasInterviewModule && !templateValue) {
        dispatch(
          interviewTemplates.postStart({
            data: getDefaultTemplate(data, templatePosition),
            noSnackbar: true,
            postSuccessAction: () =>
              patchJobTemplate({
                interview_template: `${templatePosition} ${t`Template`}`,
              }),
          }),
        )
      } else {
        patchJobTemplate()
      }
    },
    [
      query,
      template,
      handleClose,
      handleGetJobTemplates,
      handlePatchJobTemplate,
      handleResetTemplate,
      handleShowSnackbar,
      dispatch,
    ],
  )

  const handleManagementJobTemplateConfirm = React.useCallback(
    async (data, { onError, onSuccess }: FormCallbacks) =>
      template
        ? await handleEditJobTemplate(data, {
            onError,
            onSuccess,
          })
        : await handleCreateJobTemplate(data, {
            onError,
            onSuccess,
          }),
    [template, handleCreateJobTemplate, handleEditJobTemplate],
  )

  return {
    handleManagementJobTemplateClose,
    handleManagementJobTemplateConfirm,
    handleManagementJobTemplateOpen: handleOpen,
    isManagementJobTemplateOpen: isOpen,
  }
}

const useCopyJobTemplateModal = (state, handlers) => {
  const dispatch = useDispatch()

  const { template } = state
  const { handlePostJobTemplate, handleResetTemplate, handleShowSnackbar } =
    handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleCopyJobTemplateClose = React.useCallback(() => {
    handleClose()
    handleResetTemplate()
  }, [handleClose, handleResetTemplate])

  const handleCopyJobTemplateConfirm = React.useCallback(
    async (
      { title, department },
      { onError }: { onError?: ApiErrorHandler } = {},
    ) => {
      const { error, meta, response } = await handlePostJobTemplate({
        ...template,
        department,
        interview_template: undefined,
        name: title,
      })

      if (response) {
        handleCopyJobTemplateClose()
        handleShowSnackbar({
          message: t`Job template was successfully copied.`,
          type: 'success',
        })
        dispatch(toTemplates(TAB_NAMES.JOBS, response?.id))
      } else {
        onError?.({ message: error, type: meta.HTTPstatus })
      }
    },
    [
      template,
      dispatch,
      handleCopyJobTemplateClose,
      handlePostJobTemplate,
      handleShowSnackbar,
    ],
  )

  return {
    handleCopyJobTemplateClose,
    handleCopyJobTemplateConfirm,
    handleCopyJobTemplateOpen: handleOpen,
    isCopyJobTemplateOpen: isOpen,
  }
}

export const useFilter = () => {
  const [filter, setFilter] = React.useState({ department: [] })
  const [departmentValue, setDepartmentValue] = React.useState([])

  const setDepartment = department => {
    setFilter({ department: department.map(dep => dep.value) })
    setDepartmentValue(department)
  }

  return {
    state: { filter, departmentValue },
    handlers: { setDepartment },
  }
}

const useDeleteJobTemplateModal = (state, handlers) => {
  const { templateId } = state
  const { handleDeleteJobTemplate, handleResetTemplate } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleDeleteJobTemplateClose = React.useCallback(() => {
    handleClose()
    handleResetTemplate()
  }, [handleClose, handleResetTemplate])

  const handleDeleteJobTemplateConfirm = React.useCallback(() => {
    handleDeleteJobTemplate(
      { templateId },
      {
        postSuccessAction: () => {
          handleDeleteJobTemplateClose()
        },
      },
    )
  }, [templateId, handleDeleteJobTemplate, handleDeleteJobTemplateClose])

  return {
    handleDeleteJobTemplateClose,
    handleDeleteJobTemplateConfirm,
    handleDeleteJobTemplateOpen: handleOpen,
    isDeleteJobTemplateOpen: isOpen,
  }
}

export const useJobTemplates = () => {
  const { query = {} } = useSelector(getLocation)

  const [template, setTemplate] = React.useState<JobTemplate | null>(null)

  const handleResetTemplate = () => setTemplate(null)

  const [search, setSearch] = useSearch()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const { handleGetJobTemplates } = useQueries()

  const {
    handleDeleteJobTemplate,
    handlePatchJobTemplate,
    handlePostJobTemplate,
  } = useMutations()

  const {
    state: { filter, departmentValue },
    handlers: { setDepartment },
  } = useFilter()

  const {
    handleManagementJobTemplateClose,
    handleManagementJobTemplateConfirm,
    handleManagementJobTemplateOpen,
    isManagementJobTemplateOpen,
  } = useManagementJobTemplateModal(
    { query, template },
    {
      handleGetJobTemplates,
      handlePatchJobTemplate,
      handlePostJobTemplate,
      handleResetTemplate,
      handleShowSnackbar,
    },
  )

  const {
    handleCopyJobTemplateClose,
    handleCopyJobTemplateConfirm,
    handleCopyJobTemplateOpen,
    isCopyJobTemplateOpen,
  } = useCopyJobTemplateModal(
    { template },
    { handlePostJobTemplate, handleResetTemplate, handleShowSnackbar },
  )

  const {
    handleDeleteJobTemplateClose,
    handleDeleteJobTemplateConfirm,
    handleDeleteJobTemplateOpen,
    isDeleteJobTemplateOpen,
  } = useDeleteJobTemplateModal(
    { templateId: template?.id },
    { handleDeleteJobTemplate, handleResetTemplate },
  )

  const handleCopy = React.useCallback(
    ({ template }) => {
      setTemplate(template)
      handleCopyJobTemplateOpen()
    },
    [handleCopyJobTemplateOpen],
  )

  const handleDelete = React.useCallback(
    ({ template }) => {
      setTemplate(template)
      handleDeleteJobTemplateOpen()
    },
    [handleDeleteJobTemplateOpen],
  )

  const handleEdit = React.useCallback(
    ({ template }) => {
      setTemplate(template)
      handleManagementJobTemplateOpen()
    },
    [handleManagementJobTemplateOpen],
  )

  return {
    state: {
      search,
      template,
      isCopyJobTemplateOpen,
      isDeleteJobTemplateOpen,
      isManagementJobTemplateOpen,
      departmentValue,
      filter,
    },
    handlers: {
      handleCopyJobTemplateClose,
      handleCopyJobTemplateConfirm,
      handleDeleteJobTemplateClose,
      handleDeleteJobTemplateConfirm,
      handleManagementJobTemplateClose,
      handleManagementJobTemplateConfirm,
      handleManagementJobTemplateOpen,
      setDepartment,
      handleCopy,
      handleDelete,
      handleEdit,
      setSearch,
    },
  }
}
